import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import {
  Plus, Edit, Trash2, AlertTriangle, Search, Eye,
  Archive, ArchiveRestore, Printer, Tag, X, CheckSquare,
} from 'lucide-react';
import { SortableHeader, applySorting } from '../components/SortableHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Recipes = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showAlertsOnly, setShowAlertsOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [benchmarkMargins, setBenchmarkMargins] = useState({});
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const handleSort = (field, dir) => { setSortField(field); setSortDirection(dir); };

  const editRecipeId = searchParams.get('edit');
  useEffect(() => {
    if (editRecipeId) navigate(`/recipes/${editRecipeId}/edit`, { replace: true });
  }, [editRecipeId, navigate]);

  useEffect(() => { fetchRecipes(); }, [showArchived]);

  const fetchRecipes = async () => {
    try {
      const params = showArchived ? { include_archived: true } : {};
      const [res, marginsRes] = await Promise.all([
        axios.get(`${API}/recipes`, { withCredentials: true, params }),
        axios.get(`${API}/settings/benchmark-margins`, { withCredentials: true }).catch(() => ({ data: { restaurant: 30, retail: 40 } })),
      ]);
      setRecipes(res.data);
      setBenchmarkMargins(marginsRes.data);
    } catch { toast.error('Failed to load recipes'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (recipeId) => {
    if (!window.confirm('Delete this recipe?')) return;
    try {
      await axios.delete(`${API}/recipes/${recipeId}`, { withCredentials: true });
      toast.success('Recipe deleted');
      fetchRecipes();
    } catch { toast.error('Failed to delete recipe'); }
  };

  const handleArchive = async (recipe) => {
    const action = recipe.archived ? 'unarchive' : 'archive';
    try {
      await axios.post(`${API}/recipes/${recipe.recipe_id}/${action}`, {}, { withCredentials: true });
      toast.success(`Recipe ${action}d`);
      fetchRecipes();
    } catch { toast.error(`Failed to ${action} recipe`); }
  };

  // Filtering
  const filteredRecipes = recipes.filter(recipe => {
    const matchesSearch = recipe.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          recipe.menu_product_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || recipe.category === filterCategory;
    const matchesAlert = !showAlertsOnly || recipe.is_cost_alert;
    return matchesSearch && matchesCategory && matchesAlert;
  });

  const benchmarkCategories = Object.keys(benchmarkMargins).map(
    k => k.charAt(0).toUpperCase() + k.slice(1)
  );

  // Selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRecipes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRecipes.map(r => r.recipe_id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());
  const selCount = selectedIds.size;
  const allSelected = filteredRecipes.length > 0 && selectedIds.size === filteredRecipes.length;
  const sortedRecipes = applySorting(filteredRecipes, sortField, sortDirection);

  // Bulk actions
  const bulkUpdateCategory = async () => {
    if (!bulkCategory) { toast.error('Select a category'); return; }
    setBulkBusy(true);
    try {
      await axios.put(`${API}/recipes/bulk/category`, {
        recipe_ids: [...selectedIds], category: bulkCategory,
      }, { withCredentials: true });
      toast.success(`Category updated for ${selCount} recipes`);
      clearSelection(); setBulkCategory('');
      fetchRecipes();
    } catch { toast.error('Failed to update categories'); }
    finally { setBulkBusy(false); }
  };

  const bulkArchive = async (archive = true) => {
    setBulkBusy(true);
    const endpoint = archive ? 'archive' : 'unarchive';
    try {
      await axios.post(`${API}/recipes/bulk/${endpoint}`, {
        recipe_ids: [...selectedIds],
      }, { withCredentials: true });
      toast.success(`${archive ? 'Archived' : 'Unarchived'} ${selCount} recipes`);
      clearSelection();
      fetchRecipes();
    } catch { toast.error(`Failed to ${endpoint} recipes`); }
    finally { setBulkBusy(false); }
  };

  const bulkDeleteRecipes = async () => {
    if (!window.confirm(`Delete ${selCount} recipes? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/recipes/bulk/delete`, {
        recipe_ids: [...selectedIds],
      }, { withCredentials: true });
      toast.success(`Deleted ${selCount} recipes`);
      clearSelection();
      fetchRecipes();
    } catch { toast.error('Failed to delete recipes'); }
    finally { setBulkBusy(false); }
  };

  const bulkPrint = () => {
    const ids = [...selectedIds].join(',');
    window.open(`/recipes/print?ids=${ids}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="recipes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Recipes
          </h1>
          <p className="text-muted-foreground mt-1">
            {recipes.length} recipes — {recipes.filter(r => r.is_cost_alert).length} cost alerts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 mr-2">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-recipes-toggle" />
            <Label className="text-sm text-muted-foreground cursor-pointer" onClick={() => setShowArchived(!showArchived)}>
              Show Archived
            </Label>
          </div>
          <Link to="/recipes/new">
            <Button data-testid="add-recipe-btn">
              <Plus className="w-4 h-4 mr-2" />Create Recipe
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by recipe or product name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="recipe-search-input"
          />
        </div>
        <Select value={filterCategory || "all"} onValueChange={(val) => setFilterCategory(val === "all" ? "" : val)}>
          <SelectTrigger className="w-full md:w-48" data-testid="recipe-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {benchmarkCategories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat} ({benchmarkMargins[cat.toLowerCase()]}%)</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={showAlertsOnly ? "destructive" : "outline"}
          onClick={() => setShowAlertsOnly(!showAlertsOnly)}
          data-testid="recipe-alerts-filter"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Alerts {showAlertsOnly ? 'ON' : ''}
        </Button>
      </div>

      {/* Bulk Action Bar */}
      {selCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg" data-testid="bulk-action-bar">
          <div className="flex items-center gap-2 mr-2">
            <CheckSquare className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">{selCount} selected</span>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={clearSelection}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>

          <Button variant="outline" size="sm" onClick={bulkPrint} disabled={bulkBusy} data-testid="bulk-print-btn">
            <Printer className="w-3.5 h-3.5 mr-1.5" />Print
          </Button>

          <div className="flex items-center gap-1.5">
            <Select value={bulkCategory || "pick"} onValueChange={(v) => setBulkCategory(v === "pick" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs w-36" data-testid="bulk-category-select">
                <SelectValue placeholder="Set category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pick" disabled>Set category...</SelectItem>
                {benchmarkCategories.map(c => (
                  <SelectItem key={c} value={c}>{c} ({benchmarkMargins[c.toLowerCase()]}%)</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={bulkUpdateCategory} disabled={bulkBusy || !bulkCategory} data-testid="bulk-category-apply">
              <Tag className="w-3.5 h-3.5 mr-1.5" />Apply
            </Button>
          </div>

          <Button variant="outline" size="sm" onClick={() => bulkArchive(true)} disabled={bulkBusy} data-testid="bulk-archive-btn">
            <Archive className="w-3.5 h-3.5 mr-1.5" />Archive
          </Button>
          <Button variant="outline" size="sm" onClick={() => bulkArchive(false)} disabled={bulkBusy} data-testid="bulk-unarchive-btn">
            <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" />Unarchive
          </Button>
          <Button variant="destructive" size="sm" onClick={bulkDeleteRecipes} disabled={bulkBusy} data-testid="bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
          </Button>
        </div>
      )}

      {/* Recipe List */}
      {filteredRecipes.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">No recipes found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || filterCategory || showAlertsOnly
                  ? 'Try adjusting your filters'
                  : 'Create a recipe by linking stock items to a menu product'}
              </p>
              {!searchQuery && !filterCategory && !showAlertsOnly && (
                <Link to="/recipes/new">
                  <Button><Plus className="w-4 h-4 mr-2" />Create Recipe</Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 pl-4">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-recipes"
                    />
                  </TableHead>
                  <TableHead><SortableHeader label="Recipe" field="name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="hidden md:table-cell"><SortableHeader label="Category" field="category" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="text-right"><SortableHeader label="Cost" field="total_cost" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                  <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="Sell (incl VAT)" field="selling_price_incl_vat" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                  <TableHead className="text-right"><SortableHeader label="Cost %" field="actual_cost_percentage" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRecipes.map((recipe) => (
                  <TableRow
                    key={recipe.recipe_id}
                    className={`${recipe.is_cost_alert ? 'bg-destructive/5' : ''} ${recipe.archived ? 'opacity-50' : ''} ${selectedIds.has(recipe.recipe_id) ? 'bg-primary/5' : ''}`}
                    data-testid={`recipe-row-${recipe.recipe_id}`}
                  >
                    <TableCell className="pl-4">
                      <Checkbox
                        checked={selectedIds.has(recipe.recipe_id)}
                        onCheckedChange={() => toggleSelect(recipe.recipe_id)}
                        data-testid={`select-recipe-${recipe.recipe_id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {recipe.image_url ? (
                          <img src={recipe.image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                        ) : null}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{recipe.name}</p>
                            {recipe.archived && <Badge variant="outline" className="text-xs bg-muted">Archived</Badge>}
                          </div>
                          {recipe.menu_product_name && recipe.menu_product_name !== recipe.name && (
                            <p className="text-xs text-muted-foreground truncate">{recipe.menu_product_name}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {recipe.category ? (
                        <Badge variant="secondary" className="text-xs">{recipe.category}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      R{recipe.total_cost?.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right hidden sm:table-cell">
                      R{recipe.selling_price_incl_vat?.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={recipe.is_cost_alert ? "destructive" : "secondary"} className="text-xs">
                        {recipe.actual_cost_percentage?.toFixed(1)}%
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleArchive(recipe)}
                          title={recipe.archived ? 'Unarchive' : 'Archive'}
                          data-testid={`archive-recipe-${recipe.recipe_id}`}>
                          {recipe.archived
                            ? <ArchiveRestore className="w-4 h-4 text-green-600" />
                            : <Archive className="w-4 h-4 text-amber-600" />}
                        </Button>
                        <Link to={`/recipes/${recipe.recipe_id}`}>
                          <Button variant="ghost" size="icon" data-testid={`view-recipe-${recipe.recipe_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Link to={`/recipes/${recipe.recipe_id}/edit`}>
                          <Button variant="ghost" size="icon" data-testid={`edit-recipe-${recipe.recipe_id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(recipe.recipe_id)} data-testid={`delete-recipe-${recipe.recipe_id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Recipes;
